"use client";

import { c as _c } from "react/compiler-runtime";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import get from "lodash/get";
import { useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { HiOutlineXCircle } from "react-icons/hi";
import { Input, Label, SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui";
import getGoogleMapsPlacesApiClient from "@/lib/google-places-client";
import { listOfStateAbbreviations } from "@/lib/state-abbreviations";
import { clickableText, cx } from "@/lib/tailwind-utils";
interface AddressInputProps {
  required?: boolean;
  className?: string;
  namePrefix?: string;
  label?: string;
  includeCoordinates?: boolean;
  defaultExpanded?: boolean;
  disabled?: boolean;
}
export default function AddressInput(t0) {
  const $ = _c(73);
  const {
    required: t1,
    className,
    namePrefix: t2,
    label: t3,
    includeCoordinates: t4,
    defaultExpanded: t5,
    disabled: t6
  } = t0;
  const required = t1 === undefined ? false : t1;
  const namePrefix = t2 === undefined ? "" : t2;
  const label = t3 === undefined ? "Address" : t3;
  const includeCoordinates = t4 === undefined ? false : t4;
  const defaultExpanded = t5 === undefined ? false : t5;
  const disabled = t6 === undefined ? false : t6;
  const [showFullForm, setShowFullForm] = useState(defaultExpanded || required);
  const [manualEntry, setManualEntry] = useState(false);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const [suggestions, setSuggestions] = useState(t7);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    register,
    control,
    setValue,
    watch,
    getValues,
    clearErrors,
    formState: t8
  } = useFormContext();
  const {
    errors
  } = t8;
  let t9;
  if ($[1] !== namePrefix) {
    t9 = key => namePrefix ? `${namePrefix}${key}` : key;
    $[1] = namePrefix;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const addPrefix = t9;
  let t10;
  if ($[3] !== addPrefix || $[4] !== watch) {
    const address = watch(addPrefix("address"));
    const city = watch(addPrefix("city"));
    const state = watch(addPrefix("state"));
    const zipCode = watch(addPrefix("zipCode"));
    t10 = address && address.trim() || city && city.trim() || state && state.trim() || zipCode && zipCode.trim();
    $[3] = addPrefix;
    $[4] = watch;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const isAnyFieldFilled = !!t10;
  const isFieldRequired = required || isAnyFieldFilled;
  let t11;
  if ($[6] !== addPrefix || $[7] !== clearErrors || $[8] !== getValues || $[9] !== required) {
    t11 = nextAddress => {
      if (required) {
        return;
      }
      const hasAnyFieldAfterChange = !!(nextAddress.trim() || getValues(addPrefix("city"))?.trim() || getValues(addPrefix("state"))?.trim() || getValues(addPrefix("zipCode"))?.trim());
      if (!hasAnyFieldAfterChange) {
        clearErrors([addPrefix("address"), addPrefix("city"), addPrefix("state"), addPrefix("zipCode")]);
      }
    };
    $[6] = addPrefix;
    $[7] = clearErrors;
    $[8] = getValues;
    $[9] = required;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const clearOptionalAddressErrorsIfEmpty = t11;
  const timeoutRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const parseAddressString = _temp2;
  const loadSuggestions = async inputValue => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!inputValue || inputValue.trim().length <= 3) {
      setSuggestions([]);
      setPopoverOpen(false);
      return;
    }
    timeoutRef.current = setTimeout(async () => {
      const places = await getGoogleMapsPlacesApiClient();
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }
      new places.AutocompleteService().getPlacePredictions({
        input: inputValue,
        sessionToken: sessionTokenRef.current,
        componentRestrictions: {
          country: "us"
        }
      }, (predictions, status) => {
        if (status === places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
          setPopoverOpen(false);
          return;
        }
        if (status !== places.PlacesServiceStatus.OK || !predictions) {
          return;
        }
        setSuggestions(predictions);
        setPopoverOpen(true);
      });
    }, 300);
  };
  const handleSuggestionSelected = async suggestion => {
    setSuggestions([]);
    setPopoverOpen(false);
    setShowFullForm(true);
    const places_0 = await getGoogleMapsPlacesApiClient();
    const sessionToken = sessionTokenRef.current || undefined;
    sessionTokenRef.current = null;
    const attributionContainer = document.getElementById("googlemaps-attribution-container");
    if (attributionContainer) {
      new places_0.PlacesService(attributionContainer as HTMLDivElement).getDetails({
        placeId: suggestion.place_id,
        fields: includeCoordinates ? ["address_components", "geometry"] : ["address_components"],
        sessionToken
      }, (place, status_0) => {
        if (status_0 === places_0.PlacesServiceStatus.OK && place) {
          const getAddressComponent = (type, t12) => {
            const short_name = t12 === undefined ? false : t12;
            return place.address_components?.find(component => component.types.includes(type))?.[short_name ? "short_name" : "long_name"] || "";
          };
          const streetNumber = getAddressComponent("street_number");
          const route = getAddressComponent("route");
          setValue(addPrefix("address"), `${streetNumber} ${route}`.trim());
          setValue(addPrefix("city"), getAddressComponent("locality") || getAddressComponent("sublocality"));
          setValue(addPrefix("state"), getAddressComponent("administrative_area_level_1", true));
          setValue(addPrefix("zipCode"), getAddressComponent("postal_code"));
          if (place.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setValue(addPrefix("latitude"), lat);
            setValue(addPrefix("longitude"), lng);
          }
        }
      });
    }
  };
  let t13;
  if ($[11] !== addPrefix || $[12] !== errors) {
    t13 = fieldName => {
      const error = get(errors, addPrefix(fieldName));
      return error?.message?.toString();
    };
    $[11] = addPrefix;
    $[12] = errors;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const getErrorMessage = t13;
  let t14;
  if ($[14] !== addPrefix || $[15] !== clearErrors || $[16] !== includeCoordinates || $[17] !== setValue) {
    t14 = () => {
      setValue(addPrefix("address"), "");
      setValue(addPrefix("addressLineTwo"), "");
      setValue(addPrefix("city"), "");
      setValue(addPrefix("state"), "");
      setValue(addPrefix("zipCode"), "");
      if (includeCoordinates) {
        setValue(addPrefix("latitude"), null);
        setValue(addPrefix("longitude"), null);
      }
      clearErrors([addPrefix("address"), addPrefix("addressLineTwo"), addPrefix("city"), addPrefix("state"), addPrefix("zipCode")]);
      setSuggestions([]);
      setPopoverOpen(false);
      setShowFullForm(false);
      setManualEntry(false);
    };
    $[14] = addPrefix;
    $[15] = clearErrors;
    $[16] = includeCoordinates;
    $[17] = setValue;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const handleClearAddress = t14;
  const showContent = !manualEntry && suggestions.length > 0;
  const showClearButton = !required && isAnyFieldFilled;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div id="googlemaps-attribution-container" />;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== addPrefix) {
    t16 = addPrefix("address");
    $[20] = addPrefix;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== isFieldRequired || $[23] !== label || $[24] !== t16) {
    t17 = <Label htmlFor={t16} mandatory={isFieldRequired}>{label}</Label>;
    $[22] = isFieldRequired;
    $[23] = label;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== disabled || $[27] !== handleClearAddress || $[28] !== showClearButton) {
    t18 = showClearButton && !disabled && <button type="button" onClick={handleClearAddress} className="flex items-center gap-1 text-caption text-muted-foreground transition-colors hover:text-foreground-secondary"><HiOutlineXCircle className="size-3.5" />Clear</button>;
    $[26] = disabled;
    $[27] = handleClearAddress;
    $[28] = showClearButton;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17 || $[31] !== t18) {
    t19 = <div className="flex items-center justify-between">{t17}{t18}</div>;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== addPrefix) {
    t20 = addPrefix("address");
    $[33] = addPrefix;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== addPrefix || $[36] !== getValues || $[37] !== isFieldRequired || $[38] !== register || $[39] !== required) {
    let t22;
    if ($[41] !== addPrefix || $[42] !== getValues || $[43] !== isFieldRequired || $[44] !== required) {
      t22 = value => {
        if (!isFieldRequired) {
          return true;
        }
        const currentCity = getValues(addPrefix("city"));
        const currentState = getValues(addPrefix("state"));
        const currentZipCode = getValues(addPrefix("zipCode"));
        const currentAddress = getValues(addPrefix("address"));
        const hasAnyFieldFilled = !!(currentAddress && currentAddress.trim() || currentCity && currentCity.trim() || currentState && currentState.trim() || currentZipCode && currentZipCode.trim());
        if ((required || hasAnyFieldFilled) && !value?.trim()) {
          return "Address is required";
        }
        return true;
      };
      $[41] = addPrefix;
      $[42] = getValues;
      $[43] = isFieldRequired;
      $[44] = required;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    t21 = register(addPrefix("address"), {
      validate: t22
    });
    $[35] = addPrefix;
    $[36] = getValues;
    $[37] = isFieldRequired;
    $[38] = register;
    $[39] = required;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[46] !== addPrefix || $[47] !== getValues || $[48] !== setValue) {
    t22 = () => {
      setTimeout(() => {
        setPopoverOpen(false);
        const currentAddress_0 = getValues(addPrefix("address"));
        const currentCity_0 = getValues(addPrefix("city"));
        const currentState_0 = getValues(addPrefix("state"));
        const currentZipCode_0 = getValues(addPrefix("zipCode"));
        const hasAddressContent = currentAddress_0?.trim();
        const isMissingOtherFields = !currentCity_0?.trim() || !currentState_0?.trim() || !currentZipCode_0?.trim();
        if (hasAddressContent && isMissingOtherFields) {
          const parsed = parseAddressString(currentAddress_0);
          if (parsed) {
            setValue(addPrefix("address"), parsed.street);
            setValue(addPrefix("city"), parsed.city);
            setValue(addPrefix("state"), parsed.state);
            setValue(addPrefix("zipCode"), parsed.zipCode);
          }
          setShowFullForm(true);
        }
      }, 200);
    };
    $[46] = addPrefix;
    $[47] = getValues;
    $[48] = setValue;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== addPrefix || $[51] !== errors) {
    t23 = get(errors, addPrefix("address"));
    $[50] = addPrefix;
    $[51] = errors;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== getErrorMessage) {
    t24 = getErrorMessage("address");
    $[53] = getErrorMessage;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== defaultExpanded || $[56] !== disabled || $[57] !== isAnyFieldFilled || $[58] !== manualEntry || $[59] !== required) {
    t25 = !isAnyFieldFilled && !manualEntry && !defaultExpanded && !required && !disabled && <p onClick={() => {
      setManualEntry(true);
      setShowFullForm(true);
    }} className={clickableText}>Fill in manually instead</p>;
    $[55] = defaultExpanded;
    $[56] = disabled;
    $[57] = isAnyFieldFilled;
    $[58] = manualEntry;
    $[59] = required;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== addPrefix || $[62] !== control || $[63] !== disabled || $[64] !== errors || $[65] !== getErrorMessage || $[66] !== getValues || $[67] !== isAnyFieldFilled || $[68] !== isFieldRequired || $[69] !== register || $[70] !== required || $[71] !== showFullForm) {
    t26 = (showFullForm || isAnyFieldFilled || required) && <div className="space-y-4"><div className="space-y-1"><Label htmlFor={addPrefix("addressLineTwo")}>Address line 2<span className="ml-1 text-caption text-foreground-muted">(Optional)</span></Label><Input id={addPrefix("addressLineTwo")} placeholder="Apartment, suite, etc. (optional)" {...register(addPrefix("addressLineTwo"))} disabled={disabled} /></div><div className="grid grid-cols-6 gap-4"><div className="col-span-2 space-y-1"><Label htmlFor={addPrefix("city")} mandatory={isFieldRequired}>City</Label><Input id={addPrefix("city")} placeholder="City" {...register(addPrefix("city"), {
            validate: value_0 => {
              if (!isFieldRequired) {
                return true;
              }
              const currentAddress_1 = getValues(addPrefix("address"));
              const currentState_1 = getValues(addPrefix("state"));
              const currentZipCode_1 = getValues(addPrefix("zipCode"));
              const currentCity_1 = getValues(addPrefix("city"));
              const hasAnyFieldFilled_0 = !!(currentAddress_1 && currentAddress_1.trim() || currentCity_1 && currentCity_1.trim() || currentState_1 && currentState_1.trim() || currentZipCode_1 && currentZipCode_1.trim());
              if ((required || hasAnyFieldFilled_0) && !value_0?.trim()) {
                return "City is required";
              }
              return true;
            }
          })} hasError={!!get(errors, addPrefix("city"))} errorMessage={getErrorMessage("city")} disabled={disabled} /></div><div className="col-span-2 space-y-1"><Label htmlFor={addPrefix("state")} mandatory={isFieldRequired}>State</Label><Controller name={addPrefix("state")} control={control} rules={{
            validate: value_1 => {
              if (!isFieldRequired) {
                return true;
              }
              const currentAddress_2 = getValues(addPrefix("address"));
              const currentCity_2 = getValues(addPrefix("city"));
              const currentZipCode_2 = getValues(addPrefix("zipCode"));
              const currentState_2 = getValues(addPrefix("state"));
              const hasAnyFieldFilled_1 = !!(currentAddress_2 && currentAddress_2.trim() || currentCity_2 && currentCity_2.trim() || currentState_2 && currentState_2.trim() || currentZipCode_2 && currentZipCode_2.trim());
              if ((required || hasAnyFieldFilled_1) && !value_1?.trim()) {
                return "State is required";
              }
              return true;
            }
          }} render={t27 => {
            const {
              field: t28,
              fieldState
            } = t27;
            const {
              onChange,
              value: value_2
            } = t28;
            return <div className="flex w-full flex-col"><SearchSelect value={value_2 || ""} onValueChange={onChange} disabled={disabled}><SearchSelectTrigger hasError={!!fieldState.error} disabled={disabled}><SearchSelectValue placeholder="State" /></SearchSelectTrigger><SearchSelectContent className="max-h-[300px] overflow-y-auto">{Object.entries(listOfStateAbbreviations).map(_temp6)}</SearchSelectContent></SearchSelect>{!!fieldState.error && <p className="mt-1 text-body text-status-error-text">{fieldState.error.message}</p>}</div>;
          }} /></div><div className="col-span-2 space-y-1"><Label htmlFor={addPrefix("zipCode")} mandatory={isFieldRequired}>ZIP code</Label><Input id={addPrefix("zipCode")} placeholder="ZIP code" {...register(addPrefix("zipCode"), {
            validate: value_3 => {
              if (!isFieldRequired) {
                return true;
              }
              const currentAddress_3 = getValues(addPrefix("address"));
              const currentCity_3 = getValues(addPrefix("city"));
              const currentState_3 = getValues(addPrefix("state"));
              const currentZipCode_3 = getValues(addPrefix("zipCode"));
              const hasAnyFieldFilled_2 = !!(currentAddress_3 && currentAddress_3.trim() || currentCity_3 && currentCity_3.trim() || currentState_3 && currentState_3.trim() || currentZipCode_3 && currentZipCode_3.trim());
              if ((required || hasAnyFieldFilled_2) && !value_3?.trim()) {
                return "ZIP code is required";
              }
              if (value_3 && value_3.trim() && !/^\d{5}(-\d{4})?$/.test(value_3.trim())) {
                return "Invalid ZIP code";
              }
              return true;
            }
          })} hasError={!!get(errors, addPrefix("zipCode"))} errorMessage={getErrorMessage("zipCode")} disabled={disabled} /></div></div></div>;
    $[61] = addPrefix;
    $[62] = control;
    $[63] = disabled;
    $[64] = errors;
    $[65] = getErrorMessage;
    $[66] = getValues;
    $[67] = isAnyFieldFilled;
    $[68] = isFieldRequired;
    $[69] = register;
    $[70] = required;
    $[71] = showFullForm;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  return <div className={className}>{t15}<div className="space-y-4"><div className="space-y-1">{t19}<PopoverPrimitives.Root open={popoverOpen && showContent} onOpenChange={setPopoverOpen}><PopoverPrimitives.Anchor asChild={true}><Input id={t20} placeholder="Enter address" {...t21} onChange={e => {
              setValue(addPrefix("address"), e.target.value);
              clearOptionalAddressErrorsIfEmpty(e.target.value);
              if (!disabled) {
                loadSuggestions(e.target.value);
              }
            }} disabled={disabled} onBlur={t22} hasError={!!t23} errorMessage={t24} /></PopoverPrimitives.Anchor>{showContent && <PopoverPrimitives.Portal><PopoverPrimitives.Content className={cx("relative z-[60] overflow-hidden rounded-md border border-border shadow-xl shadow-black/[2.5%]", "min-w-[calc(var(--radix-popover-trigger-width))] max-w-[95vw]", "max-h-60", "bg-background/90 backdrop-blur-sm", "will-change-[transform,opacity]", "data-[state=closed]:animate-hide", "data-[side=bottom]:animate-slide-down-and-fade data-[side=left]:animate-slide-left-and-fade data-[side=right]:animate-slide-right-and-fade data-[side=top]:animate-slide-up-and-fade", "data-[side=bottom]:mt-1.5")} sideOffset={0} align="start" onOpenAutoFocus={_temp3} onCloseAutoFocus={_temp4}><div className="max-h-60 overflow-auto p-1">{suggestions.map(suggestion_0 => <div key={suggestion_0.place_id} className="cursor-pointer rounded px-2 py-2.5 hover:bg-hover md:py-1.5" onMouseDown={_temp5} onClick={() => handleSuggestionSelected(suggestion_0)}><div className="text-body">{suggestion_0.structured_formatting.main_text}</div><div className="text-caption text-muted-foreground">{suggestion_0.structured_formatting.secondary_text}</div></div>)}</div></PopoverPrimitives.Content></PopoverPrimitives.Portal>}</PopoverPrimitives.Root>{t25}</div>{t26}</div></div>;
}
function _temp6(t0) {
  const [abbr, name] = t0;
  return <SearchSelectItem key={abbr} value={abbr}>{name}</SearchSelectItem>;
}
function _temp5(e_2) {
  e_2.preventDefault();
}
function _temp4(e_1) {
  e_1.preventDefault();
}
function _temp3(e_0) {
  e_0.preventDefault();
}
function _temp2(fullAddress) {
  const trimmed = fullAddress.trim();
  const zipMatch = trimmed.match(/\b(\d{5}(?:-\d{4})?)\s*$/);
  if (!zipMatch) {
    return null;
  }
  const parsedZip = zipMatch[1];
  const withoutZip = trimmed.slice(0, zipMatch.index ?? trimmed.length).trim();
  const statePattern = new RegExp(`[,\\s]+(${Object.keys(listOfStateAbbreviations).join("|")})\\s*$`, "i");
  const stateMatch = withoutZip.match(statePattern);
  if (!stateMatch) {
    return null;
  }
  const parsedState = stateMatch[1].toUpperCase();
  const withoutState = withoutZip.slice(0, stateMatch.index ?? withoutZip.length).trim();
  const parts = withoutState.split(",").map(_temp);
  if (parts.length < 2) {
    return null;
  }
  const parsedCity = parts.pop() || "";
  const parsedStreet = parts.join(", ");
  if (!parsedStreet || !parsedCity) {
    return null;
  }
  return {
    street: parsedStreet,
    city: parsedCity,
    state: parsedState,
    zipCode: parsedZip
  };
}
function _temp(p) {
  return p.trim();
}